import { Link } from "react-router-dom";
import { ArrowRight, MessageSquareText, Camera, WalletCards, Navigation } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal, MaskedLines } from "../components/site/Reveal";
import { StoreBadges } from "../components/site/PlayBadge";

const HIGHLIGHTS = [
  {
    n: "01",
    icon: MessageSquareText,
    title: "Anything, from any shop",
    body: "Type or voice — “atta 2kg” — a nearby rider goes and gets it.",
  },
  {
    n: "02",
    icon: Camera,
    title: "Photo + real price",
    body: "The rider snaps the actual item at the shop. You approve before they pay.",
  },
  {
    n: "03",
    icon: WalletCards,
    title: "Pay after you approve",
    body: "Wallet, UPI, card or cash. Flat ₹30 delivery + 10% platform. No hidden fees. No GST.",
  },
  {
    n: "04",
    icon: Navigation,
    title: "Live tracking + OTP",
    body: "Follow the rider on the map. 4-digit OTP handover at your door.",
  },
];

export default function Landing() {
  useSEO({
    title: "Fetcher — Buy anything, from any local shop. Delivered in 30 minutes.",
    description:
      "Tell us what you need. A nearby rider fetches it from any store — even shops that aren't online. Flat ₹30 delivery, live tracking and OTP handover.",
    path: "/",
  });

  return (
    <main data-testid="landing-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-[#EC6C2B]"
      >
        {/* Desktop: full-bleed banner background (mascot + phone on the right) */}
        <div
          className="absolute inset-0 hidden bg-cover bg-center lg:block"
          style={{ backgroundImage: "url('/hero-banner.webp')" }}
        />
        <div className="relative mx-auto flex max-w-7xl flex-col px-5 pb-20 pt-32 sm:px-8 lg:min-h-[640px] lg:justify-center lg:pb-28 lg:pt-40">
          <div className="lg:max-w-xl">
            <MaskedLines
              className="text-white"
              lineClassName="text-5xl font-extrabold leading-[1.12] tracking-tight sm:text-6xl lg:text-[5rem]"
              lines={["Buy anything.", "From any", "local shop."]}
            />
            <Reveal delay={0.55} className="mt-6 max-w-md text-base font-medium leading-relaxed text-white/90 sm:text-lg">
              Tell us what you need. A nearby rider fetches it from any store — even shops that aren't online.
              Delivered in 30 minutes.
            </Reveal>
            <Reveal delay={0.7} className="mt-8 flex flex-col items-start gap-4">
              <StoreBadges prefix="hero" />
            </Reveal>
          </div>

          {/* Mobile/tablet: show the banner below the copy */}
          <img
            src="/hero-banner.webp"
            alt="Fetcher mascot holding a phone with the Fetcher app"
            loading="eager"
            className="mt-12 block w-full rounded-3xl shadow-[0_30px_60px_rgba(0,0,0,0.25)] lg:hidden"
          />
        </div>
      </section>


      {/* HIGHLIGHTS */}
      <section data-testid="highlights-section" className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D95A17]">How Fetcher works</span>
            <h2 className="mt-3 max-w-2xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Four reasons it just feels effortless.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal
                key={h.n}
                delay={i * 0.08}
                data-testid={`highlight-card-${h.n}`}
                className="group flex flex-col rounded-[1.25rem] border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(232,110,40,0.18)]"
              >
                <div className="flex items-center justify-between">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#FFEDD5] text-[#D95A17] transition-colors group-hover:bg-[#E86E28] group-hover:text-white">
                    <h.icon className="h-5 w-5" />
                  </span>
                  <span className="text-3xl font-black tracking-tighter text-slate-100 transition-colors group-hover:text-[#E86E28]">
                    {h.n}
                  </span>
                </div>
                <h3 className="mt-6 text-xl font-extrabold tracking-tight text-slate-900">{h.title}</h3>
                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-500">{h.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOR RIDERS */}
      <section data-testid="riders-section" className="relative overflow-hidden bg-[#3A2E7E] py-20 text-white lg:py-28">
        <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#E86E28]/20 blur-3xl" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 sm:px-8 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#E86E28]">For riders</span>
            <h2 className="mt-3 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              Earn on every delivery. 100% yours.
            </h2>
            <p className="mt-5 max-w-xl text-base font-medium leading-relaxed text-white/75 sm:text-lg">
              No commission. No target pressure. Keep every rupee of the delivery fee and every rupee of the tip.
              Withdraw anytime to your UPI.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:justify-self-end">
            <Link
              to="/riders#rider-signup"
              data-testid="rider-signup-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-[#E86E28] px-8 py-4 text-lg font-extrabold text-white shadow-[0_14px_40px_rgba(232,110,40,0.45)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#D95A17]"
            >
              Sign up as rider
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="final-cta-section"
        className="bg-gradient-to-tr from-[#FFF7ED] via-[#FFEDD5] to-[#FFE4E6] py-24 text-center lg:py-32"
      >
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-6xl">Ready when you are.</h2>
            <p className="mx-auto mt-5 max-w-xl text-base font-medium leading-relaxed text-slate-600 sm:text-lg">
              Grab Fetcher on your phone — free, no signup wall.
            </p>
            <div className="mt-8 flex justify-center">
              <StoreBadges prefix="final" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
